import React from "react";
import {
  commissionMatchesBookingCarType,
  formatCommissionTimeWindow,
  getIntegrationCommissions,
} from "../utils";

const IntegrationClientCard = ({ booking }) => {
  if (!booking) return null;

  const integration =
    booking.bookingIntegration || booking.BookingIntegration;
  const hasIntegration =
    integration &&
    typeof integration === "object" &&
    (integration.id || integration.integrationType);

  const client = booking.client || booking.Client;
  const hasClient =
    client && typeof client === "object" && (client.id || client.email);

  const commissions = hasIntegration ? getIntegrationCommissions(integration) : [];
  const carType = booking.carType || null;

  if (hasIntegration) {
    return (
      <section className="logs-integration-card">
        <div className="logs-integration-card__head">
          <div>
            <h3>Booking integration</h3>
            <p className="logs-integration-card__sub">
              Channel settings and commission rules by vehicle type
            </p>
          </div>
          {integration.integrationType ? (
            <span className="logs-integration-type-pill">
              {integration.integrationType}
            </span>
          ) : null}
        </div>

        <p className="logs-integration-car-hint">
          This booking car type:{" "}
          <strong>{carType || "—"}</strong>
        </p>

        <dl className="logs-integration-meta">
          {integration.id ? (
            <div className="logs-integration-meta__row">
              <dt>Integration ID</dt>
              <dd>{integration.id}</dd>
            </div>
          ) : null}
          {integration.clientId != null && integration.clientId !== "" ? (
            <div className="logs-integration-meta__row">
              <dt>External client ID</dt>
              <dd>{String(integration.clientId)}</dd>
            </div>
          ) : null}
          {integration.companyId ? (
            <div className="logs-integration-meta__row">
              <dt>Company user ID</dt>
              <dd>{integration.companyId}</dd>
            </div>
          ) : null}
          {integration.roundable != null ? (
            <div className="logs-integration-meta__row">
              <dt>Roundable</dt>
              <dd>{integration.roundable ? "Yes" : "No"}</dd>
            </div>
          ) : null}
          {integration.commission != null && integration.commission !== "" ? (
            <div className="logs-integration-meta__row">
              <dt>Default commission</dt>
              <dd>{String(integration.commission)}</dd>
            </div>
          ) : null}
        </dl>

        {commissions.length ? (
          <div className="logs-commissions-block">
            <h4>Commissions by service level</h4>
            <p className="logs-commissions-hint">
              Rows matching this booking&apos;s car type are highlighted. &quot;Apply&quot;
              indicates the rule is active for that tier.
            </p>
            <div className="logs-commissions-table-wrap">
              <table className="logs-commissions-table">
                <thead>
                  <tr>
                    <th>Service level</th>
                    <th>Commission</th>
                    <th>Apply</th>
                    <th>Time window</th>
                    <th>Roundable</th>
                  </tr>
                </thead>
                <tbody>
                  {commissions.map((row) => {
                    const matches = commissionMatchesBookingCarType(row, carType);
                    const applied = Boolean(row.apply);
                    return (
                      <tr
                        key={row.id || `${row.serviceLevel}-${row.commission}`}
                        className={
                          matches
                            ? applied
                              ? "logs-commissions-row--match"
                              : "logs-commissions-row--match-muted"
                            : undefined
                        }
                      >
                        <td>
                          {row.serviceLevel || "—"}
                          {matches ? (
                            <span
                              className={
                                applied
                                  ? "logs-commissions-badge"
                                  : "logs-commissions-badge logs-commissions-badge--muted"
                              }
                            >
                              {applied
                                ? "This booking"
                                : "Same car type (apply off)"}
                            </span>
                          ) : null}
                        </td>
                        <td>
                          {row.commission != null ? String(row.commission) : "—"}
                        </td>
                        <td>{row.apply ? "Yes" : "No"}</td>
                        <td>
                          {formatCommissionTimeWindow(row.fromTime, row.toTime)}
                        </td>
                        <td>
                          {row.roundable == null
                            ? "—"
                            : row.roundable
                              ? "Yes"
                              : "No"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <p className="logs-integration-empty-inline">
            No per–service-level commission rows on this integration.
          </p>
        )}
      </section>
    );
  }

  if (hasClient) {
    const name = [client.firstName, client.lastName]
      .filter(Boolean)
      .join(" ")
      .trim();

    return (
      <section className="logs-integration-card logs-integration-card--client">
        <div className="logs-integration-card__head">
          <div>
            <h3>Client</h3>
            <p className="logs-integration-card__sub">
              Linked client record (no booking integration on this trip)
            </p>
          </div>
        </div>

        <div className="logs-client-body">
          {client.imageUrl ? (
            <img
              src={client.imageUrl}
              alt=""
              className="logs-client-avatar"
            />
          ) : (
            <div className="logs-client-avatar logs-client-avatar--ph">
              {(name || client.companyName || "?").slice(0, 1).toUpperCase()}
            </div>
          )}
          <dl className="logs-client-dl">
            {client.id ? (
              <div className="logs-client-row">
                <dt>Client ID</dt>
                <dd>{client.id}</dd>
              </div>
            ) : null}
            {name ? (
              <div className="logs-client-row">
                <dt>Name</dt>
                <dd>{name}</dd>
              </div>
            ) : null}
            {client.companyName ? (
              <div className="logs-client-row">
                <dt>Company</dt>
                <dd>{client.companyName}</dd>
              </div>
            ) : null}
            {client.email ? (
              <div className="logs-client-row">
                <dt>Email</dt>
                <dd>
                  <a href={`mailto:${client.email}`}>{client.email}</a>
                </dd>
              </div>
            ) : null}
            {client.phoneNumber ? (
              <div className="logs-client-row">
                <dt>Phone</dt>
                <dd>
                  <a href={`tel:${client.phoneNumber}`}>{client.phoneNumber}</a>
                </dd>
              </div>
            ) : null}
          </dl>
        </div>
      </section>
    );
  }

  return (
    <section className="logs-integration-card logs-integration-card--empty">
      <h3>Integration &amp; client</h3>
      <p className="logs-integration-card__sub">
        No booking integration and no linked client record on this payload.
      </p>
      {booking.clientId ? (
        <p className="logs-integration-empty-id">
          Raw <code>clientId</code> on booking: {booking.clientId}
        </p>
      ) : null}
    </section>
  );
};

export default IntegrationClientCard;
